/**
 * Redux reducer for the Analytics Reports module (adminReportsReducer).
 * Manages all state under `adminReportsReducer.paymentReport` in the store.
 *
 * State is split across 5 APIs — each has its own reducer helper named after its service function
 * and mapper functions that transform raw API payloads into typed state shapes.
 * The raw API response is never stored directly; always go through a mapper.
 */
import _ from 'lodash';
import * as reportsActions from 'src/redux/actions/admin/reportsAction';
import { IReducerAction, IReducer, IPayloadObj } from '../_commonInterface';

// ─── API 1 interfaces ────────────────────────────────────────────────────────

export interface ITopCards {
  currentCharges: number | string;
  outstandingCharges: number | string;
  totalPayable: number | string;
  paidAmount: number | string;
}

export interface ILatestMonthDatum {
  label: string;
  value: number;
}

export interface ICompanyChartData {
  collectiveContractAccountNo: string;
  businessType: string;
  companyName: string;
  topCards: ITopCards;
  latestMonth: ILatestMonthDatum[];
  unpaidCA: number;
}

// ─── API 2 interfaces ────────────────────────────────────────────────────────

export interface IContractAccountRow {
  accountNumber: string;
  companyName: string;
  collectiveContractAccountNo: string;
  invoiceDate: string; // raw YYYYMMDD string from the API — components must parse before display
  currentCharges: number;
  outstandingCharges: number;
  totalPayable: number;
  paidAmount: number;
}

// ─── API 3 interfaces ────────────────────────────────────────────────────────

export interface IPaymentDistItem {
  amount: number;
  transactions: number;
}

export interface IPaymentDistributionData {
  totalAmount: number;
  totalTransactions: number;
  others: IPaymentDistItem;
  myTNBiz: {
    amount: number;
    transactions: number;
    totalPayablePayment: IPaymentDistItem;
    currentChargesPayment: IPaymentDistItem;
    flexiblePayment: IPaymentDistItem;
  };
}

export interface IPaymentDistributionCompany extends IPaymentDistributionData {
  collectiveContractAccountNo: string;
}

// ─── API 4 interfaces ────────────────────────────────────────────────────────

export interface IBillsVsPaymentsDatum {
  month: string;
  invoiceAmount: number;
  paymentAmount: number;
  isFiller?: boolean;
}

export interface IBillsVsPaymentsCompany {
  collectiveContractAccountNo: string;
  billsVsPayments: IBillsVsPaymentsDatum[];
}

// ─── API 5 interfaces ────────────────────────────────────────────────────────

export interface IChannelDistributionDatum {
  channel: string;
  amount: number;
  transactionCount: number;
}

export interface IChannelDistributionCompany {
  collectiveContractAccountNo: string;
  channelDistribution: IChannelDistributionDatum[];
}

// ─── Reducer state ────────────────────────────────────────────────────────────

interface IReportsReducer {
  paymentReport: {
    // API 1
    topCards: ITopCards;
    table: object[];
    tableCount: number;
    latestMonth: ILatestMonthDatum[];
    companies: ICompanyChartData[];

    // API 2
    contractAccounts: IContractAccountRow[];

    // API 3
    paymentDistribution: IPaymentDistributionData | null;
    paymentDistributionCompanies: IPaymentDistributionCompany[];

    // API 4
    billsVsPayments: IBillsVsPaymentsDatum[];
    billsVsPaymentsCompanies: IBillsVsPaymentsCompany[];

    // API 5
    channelDistribution: IChannelDistributionDatum[];
    channelDistributionCompanies: IChannelDistributionCompany[];
  };
}

// Dash strings ('-') are the safe display fallback shown on top cards before API 1 resolves.
// Do not replace with 0 — the UI renders '-' to indicate "not yet loaded", not "zero value".
const defaultState: IReportsReducer = {
  paymentReport: {
    topCards: {
      currentCharges: '-',
      outstandingCharges: '-',
      totalPayable: '-',
      paidAmount: '-',
    },
    table: [],
    tableCount: 0,
    latestMonth: [],
    companies: [],
    contractAccounts: [],
    paymentDistribution: null,
    paymentDistributionCompanies: [],
    billsVsPayments: [],
    billsVsPaymentsCompanies: [],
    channelDistribution: [],
    channelDistributionCompanies: [],
  },
};

// ─── Mappers ─────────────────────────────────────────────────────────────────

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Formats a period spanning startDate..endDate to "MMM-YY" or "MMM-YY / MMM-YY"
const formatPeriodLabel = (startDate: number | string, endDate: number | string): string => {
  const s = String(startDate);
  const e = String(endDate);
  const startMonth = parseInt(s.slice(4, 6), 10) - 1;
  const startYear = s.slice(2, 4);
  const endMonth = parseInt(e.slice(4, 6), 10) - 1;
  const endYear = e.slice(2, 4);

  if (startMonth === endMonth && startYear === endYear) {
    return `${MONTH_LABELS[startMonth]}-${startYear}`;
  }
  return `${MONTH_LABELS[startMonth]}-${startYear} / ${MONTH_LABELS[endMonth]}-${endYear}`;
};

// API 1: maps totalLatestMonthCharges to ITopCards
// Live field name: outstandingCharges (fixed spelling from legacy "outstandaingCharges")
const mapCards = (src: any): ITopCards => ({
  currentCharges: _.get(src, 'currentCharges', '-'),
  outstandingCharges: _.get(src, 'outstandingCharges', '-'),
  totalPayable: _.get(src, 'totalPayable', '-'),
  paidAmount: _.get(src, 'paidAmount', '-'),
});

// API 1: maps totalLatestMonthCharges to bar chart data points
const mapLatestMonth = (src: any): ILatestMonthDatum[] => [
  { label: 'Current Charges', value: _.get(src, 'currentCharges', 0) },
  { label: 'Outstanding Charges', value: _.get(src, 'outstandingCharges', 0) },
  { label: 'Total Payable', value: _.get(src, 'totalPayable', 0) },
  { label: 'Paid Amount', value: _.get(src, 'paidAmount', 0) },
];

// API 4: maps an array of { startDate, endDate, invoiceAmount, paidAmount } to chart data
// startDate / endDate may be numbers or strings (both formats accepted)
const mapBillsVsPayments = (src: any[]): IBillsVsPaymentsDatum[] =>
  _.map(src, (item) => ({
    month: formatPeriodLabel(item.startDate, item.endDate),
    invoiceAmount: item.invoiceAmount,
    paymentAmount: item.paidAmount,
  }));

// API 5: maps an array of { channel, amount, transactions } to chart data
const mapChannelDistribution = (src: any[]): IChannelDistributionDatum[] =>
  _.map(src, (item) => ({
    channel: item.channel,
    amount: item.amount,
    transactionCount: item.transactions ?? item.transactionCount ?? 0,
  }));

// API 3: maps overAllSummary / individual company data to IPaymentDistributionData
// Sub-objects (totalPayablePayment, currentChargesPayment, flexiblePayment) may be null in live API
const mapPaymentDistribution = (src: any): IPaymentDistributionData | null => {
  if (!src) return null;
  return {
    totalAmount: _.get(src, 'totalAmount', 0),
    totalTransactions: _.get(src, 'totalTransactions', 0),
    others: {
      amount: _.get(src, 'others.amount', 0),
      transactions: _.get(src, 'others.transactions', 0),
    },
    myTNBiz: {
      amount: _.get(src, 'myTNBiz.amount', 0),
      transactions: _.get(src, 'myTNBiz.transactions', 0),
      totalPayablePayment: {
        amount: _.get(src, 'myTNBiz.totalPayablePayment.amount', 0),
        transactions: _.get(src, 'myTNBiz.totalPayablePayment.transactions', 0),
      },
      currentChargesPayment: {
        amount: _.get(src, 'myTNBiz.currentChargesPayment.amount', 0),
        transactions: _.get(src, 'myTNBiz.currentChargesPayment.transactions', 0),
      },
      flexiblePayment: {
        amount: _.get(src, 'myTNBiz.flexiblePayment.amount', 0),
        transactions: _.get(src, 'myTNBiz.flexiblePayment.transactions', 0),
      },
    },
  };
};

// ─── Shared reducer helpers ───────────────────────────────────────────────────

// reduceCompaniesDistribution (API 1): payload shape = { overAllSummary: { totalLatestMonthCharges }, companySummary: [...] }
// collectiveContractAccountNo arrives as a number from the API → stored as string
const reduceCompaniesDistribution = (state: IReportsReducer, payload: any): IReportsReducer => {
  const overall = _.get(payload, 'overAllSummary', {});
  const chargesSummary = _.get(overall, 'totalLatestMonthCharges', {});

  return {
    ...state,
    paymentReport: {
      ...state.paymentReport,
      topCards: mapCards(chargesSummary),
      latestMonth: mapLatestMonth(chargesSummary),
      table: _.map(_.get(payload, 'companySummary', []), (company) => ({
        companyName: company.companyName,
        collectiveContractAccountNo: String(company.collectiveContractAccountNo),
        businessType: company.businessType,
        currentCharges: _.get(company, 'totalLatestMonthCharges.currentCharges', 0),
        outstandingCharges: _.get(company, 'totalLatestMonthCharges.outstandingCharges', 0),
        totalPayable: _.get(company, 'totalLatestMonthCharges.totalPayable', 0),
        paidAmount: _.get(company, 'totalLatestMonthCharges.paidAmount', 0),
        unpaidCA: company.unpaidCA,
      })),
      tableCount: _.get(payload, 'companySummary', []).length,
      companies: _.map(_.get(payload, 'companySummary', []), (company): ICompanyChartData => ({
        collectiveContractAccountNo: String(company.collectiveContractAccountNo),
        companyName: company.companyName,
        businessType: company.businessType,
        topCards: mapCards(_.get(company, 'totalLatestMonthCharges', {})),
        latestMonth: mapLatestMonth(_.get(company, 'totalLatestMonthCharges', {})),
        unpaidCA: company.unpaidCA,
      })),
    },
  };
};

// reduceAccountsChargeDistribution (API 2): payload shape = { records, accounts: [{ ContractAccountNo, companyName, CollectiveContractAccountNo, ... }] }
const reduceAccountsChargeDistribution = (state: IReportsReducer, payload: any): IReportsReducer => ({
  ...state,
  paymentReport: {
    ...state.paymentReport,
    contractAccounts: _.map(_.get(payload, 'accounts', []), (item): IContractAccountRow => ({
      accountNumber: item.ContractAccountNo,
      companyName: item.companyName,
      collectiveContractAccountNo: String(item.CollectiveContractAccountNo),
      invoiceDate: item.invoiceDate ?? '',
      currentCharges: item.currentCharges,
      outstandingCharges: item.outstandingCharges,
      totalPayable: item.totalPayable,
      paidAmount: item.paidAmount,
    })),
  },
});

// reducePaymentsDistribution (API 3): payload shape = { overAllSummary: { totalAmount, ... }, companySummary: [{ collectiveContractAccountNo, ... }] }
const reducePaymentsDistribution = (state: IReportsReducer, payload: any): IReportsReducer => ({
  ...state,
  paymentReport: {
    ...state.paymentReport,
    paymentDistribution: mapPaymentDistribution(_.get(payload, 'overAllSummary', null)),
    paymentDistributionCompanies: _.map(_.get(payload, 'companySummary', []), (co): IPaymentDistributionCompany => ({
      collectiveContractAccountNo: String(co.collectiveContractAccountNo),
      ...mapPaymentDistribution(co)!,
    })),
  },
});

// reduceBillsPaymentDistribution (API 4): payload shape = { overAllSummary: { paymentDistribution: [...] }, companySummary: [{ collectiveContractAccountNo, billsVsPayments }] }
const reduceBillsPaymentDistribution = (state: IReportsReducer, payload: any): IReportsReducer => ({
  ...state,
  paymentReport: {
    ...state.paymentReport,
    billsVsPayments: mapBillsVsPayments(_.get(payload, 'overAllSummary.paymentDistribution', [])),
    billsVsPaymentsCompanies: _.map(_.get(payload, 'companySummary', []), (co): IBillsVsPaymentsCompany => ({
      collectiveContractAccountNo: String(co.collectiveContractAccountNo),
      billsVsPayments: mapBillsVsPayments(co.billsVsPayments ?? []),
    })),
  },
});

// reducePaymentsChannelDistribution (API 5): payload shape = { overAllSummary: { paymentDistribution: [...] }, companySummary: [{ collectiveContractAccountNo, paymentDistribution }] }
const reducePaymentsChannelDistribution = (state: IReportsReducer, payload: any): IReportsReducer => ({
  ...state,
  paymentReport: {
    ...state.paymentReport,
    channelDistribution: mapChannelDistribution(_.get(payload, 'overAllSummary.paymentDistribution', [])),
    channelDistributionCompanies: _.map(_.get(payload, 'companySummary', []), (co): IChannelDistributionCompany => ({
      collectiveContractAccountNo: String(co.collectiveContractAccountNo),
      channelDistribution: mapChannelDistribution(co.paymentDistribution ?? []),
    })),
  },
});

// ─── Reducers ─────────────────────────────────────────────────────────────────

// Dispatch table: maps each action type to its dedicated reducer helper.
// Add a new entry here when a new API action is introduced — the main reducer function below
// delegates to this table and returns the unchanged state if the action type is not listed.
const REDUCERS: IReducer = {
  [reportsActions.GET_COMPANY_DISTRIBUTION]: (state: IReportsReducer, { payload }: IPayloadObj) => reduceCompaniesDistribution(state, payload),
  [reportsActions.GET_ACCOUNTS_CHARGES_DISTRIBUTION]: (state: IReportsReducer, { payload }: IPayloadObj) => reduceAccountsChargeDistribution(state, payload),
  [reportsActions.GET_PAYMENTS_DISTRIBUTION]: (state: IReportsReducer, { payload }: IPayloadObj) => reducePaymentsDistribution(state, payload),
  [reportsActions.GET_BILLS_PAYMENT_DISTRIBUTION]: (state: IReportsReducer, { payload }: IPayloadObj) => reduceBillsPaymentDistribution(state, payload),
  [reportsActions.GET_PAYMENTS_CHANNEL_DISTRIBUTION]: (state: IReportsReducer, { payload }: IPayloadObj) => reducePaymentsChannelDistribution(state, payload),
};

// Root reducer — registered in the store as `adminReportsReducer`.
// Access state in components via: useStateWithPaths(["adminReportsReducer.paymentReport"]).
const adminReportsReducer = (state: IReportsReducer = defaultState, { type, payload, error }: IReducerAction = { type: '' }) => {
  if (!type) return state;
  if (REDUCERS[type]) {
    return REDUCERS[type](state, { payload, error });
  }
  return state;
};

export default adminReportsReducer;